import React, { useState, useEffect, useRef } from 'react';
import StartMenu from './StartMenu';
import ContextMenu from '../ContextMenu/ContextMenu';
import NotificationCenter from '../Notifications/NotificationCenter';

const Taskbar = ({ theme, toggleTheme, openApps, launchApp, closeApp }) => {
  const [showStartMenu, setShowStartMenu] = useState(false);
  const [time, setTime] = useState(new Date());
  const [taskbarHovered, setTaskbarHovered] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const searchInputRef = useRef(null);
  
  // System tray states
  const [showWifiMenu, setShowWifiMenu] = useState(false);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [wifiEnabled, setWifiEnabled] = useState(true);
  const [volumeLevel, setVolumeLevel] = useState(75);
  const [isMuted, setIsMuted] = useState(false);
  const [availableNetworks, setAvailableNetworks] = useState([
    { id: 1, name: 'Home WiFi', secured: true, connected: true, strength: 95 },
    { id: 2, name: 'Neighbor WiFi', secured: true, connected: false, strength: 70 },
    { id: 3, name: 'Coffee Shop', secured: false, connected: false, strength: 60 },
    { id: 4, name: 'Free Public WiFi', secured: false, connected: false, strength: 45 },
    { id: 5, name: 'Guest Network', secured: true, connected: false, strength: 30 }
  ]);
  const [networkInfo, setNetworkInfo] = useState({
    type: 'unknown',
    online: navigator.onLine,
    downlink: null,
    rtt: null,
    effectiveType: null,
    saveData: null
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Detect actual network information using Navigator API
  useEffect(() => {
    // Update online status initially and on change
    const updateOnlineStatus = () => {
      setNetworkInfo(prev => ({
        ...prev,
        online: navigator.onLine
      }));
      
      // If we went offline, update WiFi status
      if (!navigator.onLine) {
        setWifiEnabled(false);
      } else if (!wifiEnabled) {
        // If we came back online but WiFi was disabled, enable it
        setWifiEnabled(true);
      }
    };
    
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    
    // Try to get connection information if available
    const connection = navigator.connection || 
                       navigator.mozConnection || 
                       navigator.webkitConnection;
    
    if (connection) {
      const updateConnectionInfo = () => {
        setNetworkInfo({
          type: connection.type || 'unknown',
          online: navigator.onLine,
          downlink: connection.downlink,
          rtt: connection.rtt,
          effectiveType: connection.effectiveType,
          saveData: connection.saveData
        });
        
        // Generate realistic network list based on connection type
        generateNetworkList(connection.type, connection.effectiveType);
      };
      
      updateConnectionInfo(); // Initial update
      connection.addEventListener('change', updateConnectionInfo);
      
      return () => {
        window.removeEventListener('online', updateOnlineStatus);
        window.removeEventListener('offline', updateOnlineStatus);
        connection.removeEventListener('change', updateConnectionInfo);
      };
    } else {
      // Fallback if connection API isn't available
      updateOnlineStatus(); // Initial update
      
      return () => {
        window.removeEventListener('online', updateOnlineStatus);
        window.removeEventListener('offline', updateOnlineStatus);
      };
    }
  }, [wifiEnabled]);
  
  // Generate a realistic network list based on connection type
  const generateNetworkList = (type, effectiveType) => {
    // Default network is the one user is connected to
    let connectedName = "Home Network";
    let connectedStrength = 90;
    
    // Set network name based on connection type
    if (type === 'wifi' || type === 'unknown') {
      connectedName = "Home WiFi";
    } else if (type === 'cellular') {
      if (effectiveType === '4g') {
        connectedName = "Cellular 4G";
        connectedStrength = 85;
      } else if (effectiveType === '3g') {
        connectedName = "Cellular 3G";
        connectedStrength = 70;
      } else if (effectiveType === '2g') {
        connectedName = "Cellular 2G";
        connectedStrength = 50;
      } else {
        connectedName = "Cellular";
        connectedStrength = 75;
      }
    } else if (type === 'ethernet') {
      connectedName = "Ethernet";
      connectedStrength = 100;
    }
    
    // Try to get potential network name from the hostname
    try {
      const hostname = window.location.hostname;
      if (hostname && hostname !== 'localhost') {
        // Extract network name from hostname
        const parts = hostname.split('.');
        if (parts.length > 1) {
          // Use the main domain name or subdomain for the network name
          const mainName = parts[parts.length - 2];
          if (mainName.length > 3 && type === 'wifi') {
            // Make it look like a WiFi name
            connectedName = mainName.charAt(0).toUpperCase() + mainName.slice(1) + "-WiFi";
          }
        }
      }
    } catch (e) {
      console.log("Error getting hostname:", e);
    }
    
    // Create networks list
    const networks = [
      { 
        id: 1, 
        name: connectedName, 
        secured: true, 
        connected: true, 
        strength: connectedStrength 
      }
    ];
    
    // Add some realistic nearby networks
    const nearbyNetworks = [
      { name: "NETGEAR152", secured: true, strength: Math.floor(Math.random() * 30) + 50 },
      { name: "TP-Link_5G", secured: true, strength: Math.floor(Math.random() * 40) + 40 },
      { name: "FiOS-92G7F", secured: true, strength: Math.floor(Math.random() * 30) + 40 },
      { name: "Xfinity", secured: false, strength: Math.floor(Math.random() * 20) + 40 },
      { name: "ATT-WiFi-2463", secured: true, strength: Math.floor(Math.random() * 25) + 35 },
      { name: "Guest_Network", secured: false, strength: Math.floor(Math.random() * 30) + 30 }
    ];
    
    // Randomly select 2-4 nearby networks
    const numNetworks = Math.floor(Math.random() * 3) + 2;
    for (let i = 0; i < numNetworks; i++) {
      const randomIndex = Math.floor(Math.random() * nearbyNetworks.length);
      const network = nearbyNetworks.splice(randomIndex, 1)[0];
      networks.push({
        id: i + 2,
        name: network.name,
        secured: network.secured,
        connected: false,
        strength: network.strength
      });
    }
    
    setAvailableNetworks(networks);
  };

  // Close popups when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close if click is outside the relevant elements
      const wifiMenu = document.getElementById('wifi-menu');
      const wifiButton = document.getElementById('wifi-button');
      const volumeControl = document.getElementById('volume-control');
      const volumeButton = document.getElementById('volume-button');
      const calendarPopup = document.getElementById('calendar-popup');
      const timeWidget = document.getElementById('time-widget');
      
      if (wifiMenu && wifiButton && !wifiMenu.contains(e.target) && !wifiButton.contains(e.target)) {
        setShowWifiMenu(false);
      }
      
      if (volumeControl && volumeButton && !volumeControl.contains(e.target) && !volumeButton.contains(e.target)) {
        setShowVolumeControl(false);
      }
      
      if (calendarPopup && timeWidget && !calendarPopup.contains(e.target) && !timeWidget.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Space to focus search bar
      if (e.ctrlKey && e.code === 'Space') {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
      
      // Alt+D to search for selected text
      if (e.altKey && e.code === 'KeyD') {
        e.preventDefault();
        const selectedText = window.getSelection().toString().trim();
        if (selectedText) {
          // Create a browser search with the selected text
          const searchQuery = encodeURIComponent(selectedText);
          const appToLaunch = {
            id: `browser-search-${Date.now()}`,
            title: `${selectedText} - DuckDuckGo`,
            icon: 'globe',
            component: 'WebBrowser',
            url: `duckduckgo://search?q=${searchQuery}`,
          };
          
          launchApp(appToLaunch);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [launchApp]);

  // Handle volume change with keyboard
  useEffect(() => {
    const handleVolumeKeys = (e) => {
      // Volume up key
      if (e.key === 'AudioVolumeUp' || (e.ctrlKey && e.key === 'ArrowUp')) {
        e.preventDefault();
        setVolumeLevel(prev => Math.min(100, prev + 5));
        if (isMuted) setIsMuted(false);
      }
      
      // Volume down key
      if (e.key === 'AudioVolumeDown' || (e.ctrlKey && e.key === 'ArrowDown')) {
        e.preventDefault();
        setVolumeLevel(prev => Math.max(0, prev - 5));
      }
      
      // Mute key
      if (e.key === 'AudioVolumeMute' || (e.ctrlKey && e.key === 'm')) {
        e.preventDefault();
        setIsMuted(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleVolumeKeys);
    return () => {
      window.removeEventListener('keydown', handleVolumeKeys);
    };
  }, [isMuted]);

  // Get WiFi icon based on connection state
  const getWifiIcon = () => {
    if (!wifiEnabled || !networkInfo.online) return 'wifi-slash';
    
    const connectedNetwork = availableNetworks.find(n => n.connected);
    if (!connectedNetwork) return 'wifi';
    
    // Return icon based on signal strength
    if (connectedNetwork.strength > 80) return 'wifi';
    if (connectedNetwork.strength > 50) return 'wifi';
    if (connectedNetwork.strength > 30) return 'wifi';
    return 'wifi';
  };

  // Get connected network name
  const getConnectedNetworkName = () => {
    if (!networkInfo.online) return 'Offline';
    
    const network = availableNetworks.find(n => n.connected);
    if (!network) return 'Not Connected';
    
    return network.name;
  };

  // Get network connection quality
  const getConnectionQuality = () => {
    if (!networkInfo.online) return 'Offline';
    if (networkInfo.type === 'ethernet') return 'Excellent';
    
    if (networkInfo.effectiveType) {
      switch(networkInfo.effectiveType) {
        case '4g': return 'Excellent';
        case '3g': return 'Good';
        case '2g': return 'Poor';
        default: return 'Unknown';
      }
    }
    
    const connectedNetwork = availableNetworks.find(n => n.connected);
    if (!connectedNetwork) return 'Unknown';
    
    if (connectedNetwork.strength > 80) return 'Excellent';
    if (connectedNetwork.strength > 60) return 'Good';
    if (connectedNetwork.strength > 40) return 'Fair';
    return 'Poor';
  };

  // Toggle WiFi on/off
  const toggleWifi = () => {
    setWifiEnabled(prev => !prev);
    
    // If turning WiFi on and we're online, generate a new network list
    if (!wifiEnabled && networkInfo.online) {
      generateNetworkList(
        networkInfo.type !== 'unknown' ? networkInfo.type : 'wifi',
        networkInfo.effectiveType
      );
    }
  };

  // Connect to a WiFi network
  const connectToNetwork = (networkId) => {
    setAvailableNetworks(prev => prev.map(network => ({
      ...network,
      connected: network.id === networkId
    })));
    // Close the menu after connecting
    setTimeout(() => {
      setShowWifiMenu(false);
    }, 500);
  };

  // Format the current date
  const formattedDate = time.toLocaleDateString(undefined, { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric' 
  });
  
  // Get volume icon based on volume level and mute state
  const getVolumeIcon = () => {
    if (isMuted) return 'volume-mute';
    if (volumeLevel === 0) return 'volume-off';
    if (volumeLevel < 50) return 'volume-down';
    return 'volume-up';
  };

  // Handle search submission
  const handleSearch = () => {
    if (searchText.trim()) {
      // Create a new app instance for the browser with the search
      const searchQuery = encodeURIComponent(searchText.trim());
      const appToLaunch = {
        id: `browser-search-${Date.now()}`, // Ensure unique ID
        title: `${searchText} - DuckDuckGo`,
        icon: 'globe',
        component: 'WebBrowser',
        url: `duckduckgo://search?q=${searchQuery}`,
      };
      
      launchApp(appToLaunch);
      setSearchText(''); // Clear the search input
    }
  };

  const toggleStartMenu = () => {
    setShowStartMenu(!showStartMenu);
  };

  // Handle click on a taskbar item
  const handleTaskItemClick = (item) => {
    // First determine what component type this item represents
    const componentType = item.component || getComponentForIcon(item.id);
    
    // Find an existing open app that matches this component type
    const existingApp = openApps.find(app => {
      // For YouTube/Privacy Tube, make a special comparison since it has unique naming
      if (componentType === 'YouTube' && app.component === 'YouTube') {
        return true;
      }
      
      // For other apps, compare by component
      return app.component === componentType;
    });
    
    if (existingApp) {
      // If app is already open, we should toggle its minimized state
      console.log(`Toggling window for app: ${existingApp.id} (${existingApp.component})`);
      
      // Dispatch a custom event to toggle the window's minimized state
      const toggleEvent = new CustomEvent('webos-toggle-window', { 
        detail: { appId: existingApp.id }
      });
      window.dispatchEvent(toggleEvent);
      
      // As a fallback for specific apps like MusicPlayer that might have special handling
      if (existingApp.component === 'MusicPlayer') {
        const musicPlayerElement = document.querySelector(`[data-component="${existingApp.component}"]`);
        if (musicPlayerElement) {
          console.log('Directly clicking MusicPlayer taskbar item as fallback');
          musicPlayerElement.click();
        }
      }
    } else {
      // Create a new app instance only if it's not already open
      const appToLaunch = {
        id: `${item.id}-${Date.now()}`, // Ensure unique ID
        title: item.title || (item.id.charAt(0).toUpperCase() + item.id.slice(1)), // Capitalize first letter
        icon: item.icon,
        component: item.component || getComponentForIcon(item.id),
        url: item.url || getUrlForIcon(item.id),
        className: item.className // Ensure we pass the className for YouTube
      };
      
      console.log("Launching app from taskbar:", appToLaunch);
      launchApp(appToLaunch);
    }
  };

  // Function to determine component based on icon id
  const getComponentForIcon = (iconId) => {
    switch (iconId) {
      case 'folder':
        return 'FileExplorer';
      case 'terminal':
        return 'Terminal';
      case 'robot':
        return 'AIAssistant';
      case 'chrome':
      case 'edge':
      case 'globe':
        return 'WebBrowser';
      default:
        console.log("Unknown icon:", iconId, "defaulting to WebBrowser");
        return 'WebBrowser';
    }
  };

  // Function to get URL for web apps
  const getUrlForIcon = (iconId) => {
    switch (iconId) {
      case 'chrome':
      case 'edge': 
      case 'globe':
        return 'duckduckgo://search';
      default:
        return '';
    }
  };

  // Taskbar actions for context menu
  const taskbarActions = {
    focusWindow: (appId) => {
      // Find the window and bring it to front
      console.log(`Focus window: ${appId}`);
      setContextMenu(null);
    },
    launchApp: (appId) => {
      // Launch the app
      const app = defaultTaskItems.find(item => item.id === appId);
      if (app) {
        handleTaskItemClick(app);
      }
      setContextMenu(null);
    },
    togglePin: (appId) => {
      // Toggle pin status
      console.log(`Toggle pin for: ${appId}`);
      setContextMenu(null);
    },
    closeWindow: (appId) => {
      // Close the window
      closeApp(appId);
      setContextMenu(null);
    },
    arrangeWindows: () => {
      // Use the global function if available
      if (window.arrangeAllWindows) {
        window.arrangeAllWindows();
      }
      setContextMenu(null);
    }
  };

  // Determine if an app is open by checking if any open app has the same component type
  const isAppOpen = (taskItemId) => {
    // Get the component type for this taskbar item
    let componentType;
    
    // Get the component directly from defaultTaskItems if possible
    const taskItem = defaultTaskItems.find(item => item.id === taskItemId);
    if (taskItem && taskItem.component) {
      componentType = taskItem.component;
    } else {
      componentType = getComponentForIcon(taskItemId);
    }
    
    // Check if any open app matches this component type
    return openApps.some(app => app.component === componentType);
  };

  // Get open apps that are not pinned to the taskbar
  const getOpenNotPinnedApps = () => {
    // Get the component types of all pinned apps
    const pinnedComponents = defaultTaskItems.map(item => item.component || getComponentForIcon(item.id));
    
    // Keep track of component types we've already shown
    const shownComponents = new Set();
    
    // Filter out apps that have their component type in the pinned list or already shown
    return openApps.filter(app => {
      // For pinned apps, we only show them if they're already in defaultTaskItems
      if (pinnedComponents.includes(app.component)) {
        return false;
      }
      
      // Special handling for YouTube component
      if (app.component === 'YouTube') {
        if (pinnedComponents.includes('YouTube')) {
          return false;
        }
        
        // Only show one YouTube instance
        if (shownComponents.has('YouTube')) {
          return false;
        }
        
        shownComponents.add('YouTube');
        return true;
      }
      
      // For other apps, only show one instance of each component type
      if (shownComponents.has(app.component)) {
        return false;
      }
      
      // Mark this component as shown and return it
      shownComponents.add(app.component);
      return true;
    });
  };

  // Get icon class
  const getIconClassName = (iconName) => {
    if (!iconName) return 'fas fa-question-circle'; // Default icon if none provided
    
    // If already a full class name like 'fas fa-folder', return as is
    if (iconName.includes('fa-')) return iconName;
    
    // Normalize icon name to lowercase
    const normalizedName = iconName.toLowerCase();
    
    // List of brand icons that use fab prefix
    const brandIcons = ['chrome', 'edge', 'spotify', 'facebook', 'twitter', 'windows', 'youtube'];
    if (brandIcons.includes(normalizedName)) {
      return `fab fa-${normalizedName}`;
    } else {
      return `fas fa-${normalizedName}`;
    }
  };

  // Map component types to icon names if not explicitly provided
  const getIconForComponent = (component) => {
    const componentIconMap = {
      'Terminal': 'terminal',
      'FileExplorer': 'folder',
      'AIAssistant': 'robot',
      'WebBrowser': 'globe',
      'Browser': 'globe',
      'YouTube': 'youtube',
      'Store': 'store',
      'Notepad': 'file-alt',
      'Calculator': 'calculator',
      'MusicPlayer': 'music',
      'Clock': 'clock',
      'Settings': 'cog'
    };
    
    return componentIconMap[component] || 'window-maximize';
  };

  // Default task items that appear in the taskbar - updated to match available apps
  const defaultTaskItems = [
    { id: 'folder', icon: 'folder', title: 'File Explorer', component: 'FileExplorer' },
    { id: 'terminal', icon: 'terminal', title: 'Terminal', component: 'Terminal' },
    { id: 'robot', icon: 'robot', title: 'Kiwi AI', component: 'AIAssistant' },
    { id: 'globe', icon: 'globe', title: 'Web Browser', component: 'WebBrowser', url: 'duckduckgo://search' },
    { id: 'youtube', icon: 'youtube', title: 'Privacy Tube', component: 'YouTube', className: 'fab fa-youtube' }
  ];

  const handleContextMenu = (e, app) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      type: 'taskbar',
      data: {
        appId: app.id,
        isOpen: isAppOpen(app.id),
        isPinned: true
      }
    });
  };

  // Handle horizontal scrolling with mouse wheel
  const handleTaskItemsScroll = (e) => {
    e.preventDefault();
    const container = e.currentTarget;
    container.scrollLeft += e.deltaY;
  };

  // Render taskbar app icons
  const renderOpenAppIcons = () => {
    return getOpenNotPinnedApps().map((app, index) => (
      <div 
        key={`open-${app.id}`} 
        className="task-item"
        title={app.title || app.name}
        data-component={app.component}
        data-appid={app.id}
        onClick={() => handleTaskItemClick(app)}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2)',
          position: 'relative',
          boxShadow: '0 0 5px rgba(255, 255, 255, 0.1)',
          margin: '0 2px',
          flexShrink: 0 /* Prevent items from shrinking */
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
          e.currentTarget.style.transform = 'translateY(-4px) scale(1.1)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = '0 0 5px rgba(255, 255, 255, 0.1)';
        }}
        onContextMenu={(e) => handleContextMenu(e, app)}
      >
        <i 
          className={app.className || getIconClassName(app.icon || getIconForComponent(app.component))} 
          style={{ 
            color: 'white',
            fontSize: '16px',
            transition: 'all 0.3s ease'
          }}
        />
        
        {/* Active indicator dot */}
        <div 
          style={{
            position: 'absolute',
            bottom: '2px',
            width: '4px',
            height: '4px',
            backgroundColor: 'white',
            borderRadius: '50%',
            transition: 'all 0.2s ease',
            boxShadow: '0 0 3px rgba(255, 255, 255, 0.7)',
            animation: 'pulse 2s infinite',
          }}
        />
      </div>
    ));
  };
  
  // Render pinned apps
  const renderPinnedApps = () => {
    return defaultTaskItems.map((item) => (
      <div 
        key={item.id} 
        className="task-item"
        title={item.title}
        data-component={item.component || getComponentForIcon(item.id)}
        data-appid={item.id}
        onClick={() => handleTaskItemClick(item)}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          backgroundColor: isAppOpen(item.id) ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2)',
          position: 'relative',
          boxShadow: isAppOpen(item.id) ? '0 0 5px rgba(255, 255, 255, 0.1)' : 'none',
          margin: '0 2px',
          flexShrink: 0 /* Prevent items from shrinking */
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = isAppOpen(item.id) ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)';
          e.currentTarget.style.transform = 'translateY(-4px) scale(1.1)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = isAppOpen(item.id) ? 'rgba(255, 255, 255, 0.2)' : 'transparent';
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = isAppOpen(item.id) ? '0 0 5px rgba(255, 255, 255, 0.1)' : 'none';
        }}
        onContextMenu={(e) => handleContextMenu(e, item)}
      >
        <i 
          className={item.className || getIconClassName(item.icon || getIconForComponent(item.component))} 
          style={{ 
            color: 'white',
            fontSize: '16px',
            transition: 'all 0.3s ease'
          }}
        />
        
        {/* Active indicator dot */}
        {isAppOpen(item.id) && (
          <div 
            style={{
              position: 'absolute',
              bottom: '2px',
              width: '4px',
              height: '4px',
              backgroundColor: 'white',
              borderRadius: '50%',
              transition: 'all 0.2s ease',
              boxShadow: '0 0 3px rgba(255, 255, 255, 0.7)',
              animation: 'pulse 2s infinite',
            }}
          />
        )}
      </div>
    ));
  };

  return (
    <div 
      className={`taskbar glass-effect ${theme === 'dark' ? 'dark-mode' : ''} ${taskbarHovered ? 'taskbar-hover' : ''} taskbar-container`}
      onMouseEnter={() => setTaskbarHovered(true)}
      onMouseLeave={() => setTaskbarHovered(false)}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'rgba(20, 20, 40, 0.6)',
        backdropFilter: 'blur(30px)',
        borderRadius: '24px',
        height: '54px',
        width: 'auto',
        maxWidth: '60%',
        minWidth: '400px',
        margin: '0 auto 10px auto', // Center the taskbar
        padding: '0 15px',
        boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'fixed',
        bottom: '10px',
        left: '0',
        right: '0',
        zIndex: '1000'
      }}
    >
      {/* Responsive styles for taskbar */}
      <style>
        {`
          @media (max-width: 768px) {
            .taskbar-container {
              max-width: 95% !important;
              padding: 0 10px !important;
              min-width: auto !important;
            }
          }
        `}
      </style>

      {/* Left section - Windows logo and search box */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Windows logo button */}
        <div 
          className="start-button" 
          onClick={toggleStartMenu}
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: showStartMenu ? 'linear-gradient(135deg, #ff4aa3, #915eff)' : 'linear-gradient(135deg, #ff4aa3, #915eff)',
            opacity: showStartMenu ? 1 : 0.9,
            transform: showStartMenu ? 'scale(0.95)' : 'scale(1)',
          }}
        >
          <div style={{
            width: '18px',
            height: '18px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            gap: '2px',
            padding: '2px'
          }}>
            <div style={{ backgroundColor: 'white', borderRadius: '2px' }}></div>
            <div style={{ backgroundColor: 'white', borderRadius: '2px' }}></div>
            <div style={{ backgroundColor: 'white', borderRadius: '2px' }}></div>
            <div style={{ backgroundColor: 'white', borderRadius: '2px' }}></div>
          </div>
        </div>
        
        {/* Search box */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '5px 10px',
          margin: '0 15px',
          width: '220px',
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 255, 255, 0.1)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        >
          <i className="fas fa-search" style={{ color: 'white', fontSize: '12px', marginRight: '8px' }}></i>
          <form onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          style={{ width: '100%', display: 'flex', alignItems: 'center' }}
          >
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search DuckDuckGo... (Ctrl+Space)"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'white',
                width: '100%',
                fontSize: '13px'
              }}
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0 5px',
                  fontSize: '10px'
                }}
                title="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </form>
        </div>
      </div>
        
      {/* Start Menu with fixed positioning */}
      {showStartMenu && (
        <div style={{ 
          position: 'absolute', 
          bottom: '55px', 
          left: '50%', 
          transform: 'translateX(-50%)',
          zIndex: 1001 
        }}>
          <StartMenu 
            launchApp={launchApp} 
            toggleTheme={toggleTheme} 
            currentTheme={theme}
            closeMenu={() => setShowStartMenu(false)}
          />
        </div>
      )}
      
      {/* Center section - Task Items */}
      <div className="task-items" style={{ 
        display: 'flex', 
        gap: '12px',
        overflow: 'auto',
        flexGrow: 1,
        margin: '0 10px',
        padding: '0 5px',
        maxWidth: 'calc(100% - 480px)', /* Allow space for left and right sections */
        msOverflowStyle: 'none', /* Hide scrollbar in IE and Edge */
        scrollbarWidth: 'none', /* Hide scrollbar in Firefox */
        WebkitOverflowScrolling: 'touch', /* Smooth scrolling on iOS */
        flexWrap: 'nowrap', /* Prevent wrapping to ensure horizontal scroll */
        overflowY: 'hidden', /* Ensure no vertical overflow */
      }}
      onWheel={handleTaskItemsScroll}
      >
        {/* Hide scrollbar for Chrome, Safari and Opera */}
        <style>
          {`
          .task-items::-webkit-scrollbar {
            display: none;
          }
          `}
        </style>
        {/* Pinned apps */}
        {renderPinnedApps()}

        {/* Currently open apps that are not pinned */}
        {renderOpenAppIcons()}
      </div>
      
      {/* Right side system tray */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Add NotificationCenter before WiFi icon */}
        <NotificationCenter />
        
        {/* Network icon */}
        <div 
          id="wifi-button"
          style={{ 
            color: 'white', 
            fontSize: '13px',
            position: 'relative',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            backgroundColor: showWifiMenu ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
            transition: 'all 0.2s ease'
          }}
          onClick={() => setShowWifiMenu(prev => !prev)}
          title={getConnectionQuality()}
        >
          <i className={`fas fa-${getWifiIcon()}`} 
            style={{ opacity: wifiEnabled ? 1 : 0.5 }}
          />
          
          {/* WiFi popup menu */}
          {showWifiMenu && (
            <div 
              id="wifi-menu"
              className="wifi-menu glass-effect"
              style={{
                position: 'absolute',
                bottom: '45px',
                right: 0,
                width: '280px',
                backgroundColor: 'rgba(30, 30, 45, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
                padding: '12px',
                zIndex: 1001,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '10px',
                padding: '5px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <span style={{ color: 'white', fontWeight: 'bold' }}>WiFi</span>
                <label className="toggle-switch" style={{ 
                  display: 'inline-block', 
                  position: 'relative', 
                  width: '40px', 
                  height: '20px'
                }}>
                  <input 
                    type="checkbox" 
                    checked={wifiEnabled && networkInfo.online} 
                    onChange={toggleWifi}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span 
                    className="slider" 
                    style={{ 
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: wifiEnabled && networkInfo.online ? '#4a90e2' : 'rgba(255, 255, 255, 0.3)',
                      borderRadius: '20px',
                      transition: '.4s'
                    }}
                  >
                    <span 
                      className="slider-button" 
                      style={{
                        position: 'absolute',
                        content: '""',
                        height: '16px',
                        width: '16px',
                        left: wifiEnabled && networkInfo.online ? '22px' : '2px',
                        bottom: '2px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: '.4s'
                      }}
                    />
                  </span>
                </label>
              </div>

              {/* Connection information */}
              <div style={{ 
                marginBottom: '15px',
                padding: '0 5px 10px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '6px' 
                }}>
                  <span>Status:</span>
                  <span style={{ 
                    color: networkInfo.online ? '#4caf50' : '#f44336',
                    fontWeight: 'bold'
                  }}>
                    {networkInfo.online ? 'Connected' : 'Disconnected'}
                  </span>
                </div>

                <div style={{ 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '6px' 
                }}>
                  <span>Connection Type:</span>
                  <span>
                    {networkInfo.type === 'unknown' ? 'WiFi' : 
                     networkInfo.type.charAt(0).toUpperCase() + networkInfo.type.slice(1)}
                  </span>
                </div>

                <div style={{ 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: networkInfo.effectiveType ? '6px' : '0'
                }}>
                  <span>Connection Quality:</span>
                  <span>{getConnectionQuality()}</span>
                </div>

                {networkInfo.effectiveType && (
                  <div style={{ 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '6px'
                  }}>
                    <span>Network Type:</span>
                    <span>{networkInfo.effectiveType.toUpperCase()}</span>
                  </div>
                )}

                {networkInfo.downlink && (
                  <div style={{ 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '6px'
                  }}>
                    <span>Download Speed:</span>
                    <span>{networkInfo.downlink} Mbps</span>
                  </div>
                )}

                {networkInfo.saveData !== null && (
                  <div style={{ 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                  }}>
                    <span>Data Saver:</span>
                    <span>{networkInfo.saveData ? 'Enabled' : 'Disabled'}</span>
                  </div>
                )}
              </div>
              
              {wifiEnabled && networkInfo.online ? (
                <div className="network-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <div style={{ color: 'white', fontSize: '12px', marginBottom: '8px', opacity: 0.7 }}>
                    Available Networks
                  </div>
                  {availableNetworks.map(network => (
                    <div 
                      key={network.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        borderRadius: '6px',
                        margin: '5px 0',
                        backgroundColor: network.connected ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => network.connected ? null : connectToNetwork(network.id)}
                      onMouseOver={(e) => {
                        if (!network.connected) {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!network.connected) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fas fa-wifi" style={{ 
                          opacity: network.strength / 100,
                          color: network.connected ? '#4a90e2' : 'white'
                        }}/>
                        <div>
                          <div>{network.name}</div>
                          <div style={{ fontSize: '10px', opacity: 0.7 }}>
                            {network.connected ? 'Connected' : network.secured ? 'Secured' : 'Open'}
                          </div>
                        </div>
                      </div>
                      {network.connected && (
                        <div style={{ color: '#4a90e2' }}>
                          <i className="fas fa-check-circle"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'white', padding: '10px', textAlign: 'center', opacity: 0.7 }}>
                  {networkInfo.online ? 'WiFi is turned off' : 'You are offline'}
                </div>
              )}
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginTop: '10px',
                padding: '8px 0',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <button 
                  style={{
                    padding: '5px 12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                  onClick={() => setShowWifiMenu(false)}
                >
                  <i className="fas fa-cog"></i>
                  Network Settings
                </button>

                {!networkInfo.online && (
                  <button 
                    style={{
                      padding: '5px 12px',
                      backgroundColor: '#4a90e2',
                      border: 'none',
                      borderRadius: '4px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    onClick={() => window.location.reload()}
                  >
                    Retry Connection
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Volume icon */}
        <div 
          id="volume-button"
          style={{ 
            color: 'white', 
            fontSize: '13px',
            position: 'relative',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            backgroundColor: showVolumeControl ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
            transition: 'all 0.2s ease'
          }}
          onClick={() => setShowVolumeControl(prev => !prev)}
          title={isMuted ? 'Sound Muted' : `Volume: ${volumeLevel}%`}
        >
          <i className={`fas fa-${getVolumeIcon()}`} 
            style={{ opacity: isMuted ? 0.5 : 1 }}
          />
          
          {/* Volume popup control */}
          {showVolumeControl && (
            <div 
              id="volume-control"
              className="volume-control glass-effect"
              style={{
                position: 'absolute',
                bottom: '45px',
                right: 0,
                width: '250px',
                backgroundColor: 'rgba(30, 30, 45, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
                padding: '15px',
                zIndex: 1001,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '10px',
                marginBottom: '10px'
              }}>
                <i 
                  className={`fas fa-${getVolumeIcon()}`} 
                  style={{ 
                    color: 'white', 
                    fontSize: '16px',
                    opacity: isMuted ? 0.5 : 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => setIsMuted(prev => !prev)}
                />
                <input 
                  type="range" 
                  min="0"
                  max="100"
                  value={volumeLevel}
                  onChange={(e) => {
                    setVolumeLevel(parseInt(e.target.value));
                    if (parseInt(e.target.value) > 0 && isMuted) {
                      setIsMuted(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    height: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                />
                <span style={{ color: 'white', fontSize: '14px', width: '30px', textAlign: 'right' }}>
                  {volumeLevel}%
                </span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginTop: '15px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '10px'
              }}>
                <button 
                  style={{
                    padding: '5px 12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                  onClick={() => setShowVolumeControl(false)}
                >
                  <i className="fas fa-cog"></i>
                  Sound Settings
                </button>
                
                <button 
                  style={{
                    padding: '5px 12px',
                    backgroundColor: isMuted ? '#4a90e2' : 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                  onClick={() => setIsMuted(prev => !prev)}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Battery icon */}
        <div style={{ color: 'white', fontSize: '14px', marginRight: '5px' }}>
          <i className="fas fa-battery-three-quarters"></i>
        </div>
        
        {/* Time & Date */}
        <div 
          id="time-widget"
          className="time-widget" 
          style={{ 
            color: 'white', 
            textAlign: 'right',
            padding: '0 5px',
            cursor: 'pointer',
            position: 'relative'
          }}
          onClick={() => setShowCalendar(prev => !prev)}
        >
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
            {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div style={{ fontSize: '10px', opacity: 0.8 }}>
            {formattedDate}
          </div>
          
          {/* Calendar popup */}
          {showCalendar && (
            <div 
              id="calendar-popup"
              className="calendar-popup glass-effect"
              style={{
                position: 'absolute',
                bottom: '45px',
                right: '0',
                width: '300px',
                backgroundColor: 'rgba(30, 30, 45, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
                padding: '15px',
                zIndex: 1001,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                  {time.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </div>
                <div style={{ 
                  fontSize: '22px', 
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  padding: '0 10px'
                }}>
                  {time.getDate()}
                </div>
              </div>
              
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '8px',
                textAlign: 'center',
                marginBottom: '15px'
              }}>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Su</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Mo</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Tu</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>We</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Th</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Fr</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Sa</div>
                
                {/* Calendar days - simplified example */}
                {Array.from({ length: 35 }).map((_, index) => {
                  const day = new Date(time.getFullYear(), time.getMonth(), index - time.getDay() + 1);
                  const isCurrentMonth = day.getMonth() === time.getMonth();
                  const isToday = day.getDate() === time.getDate() && isCurrentMonth;
                  
                  return (
                    <div 
                      key={index}
                      style={{
                        padding: '5px',
                        borderRadius: '50%',
                        backgroundColor: isToday ? '#4a90e2' : 'transparent',
                        opacity: isCurrentMonth ? 1 : 0.3,
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      {day.getDate()}
                    </div>
                  );
                })}
              </div>
              
              <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px' }}>
                {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
              
              <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '10px' }}>
                <button 
                  style={{
                    padding: '5px 12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                  onClick={() => setShowCalendar(false)}
                >
                  <i className="fas fa-cog"></i>
                  Date & Time Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu 
          x={contextMenu.x}
          y={contextMenu.y}
          menuType={contextMenu.type}
          data={contextMenu.data}
          actions={taskbarActions}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Add keyframes for animation */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.5); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default Taskbar; 